import React from 'react';
import { CalendarDays, Clock3, Loader2, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AttendanceTable, TONE } from '@/components/myPay/AttendanceTable';
import { PaySection } from '@/components/myPay/PaySection';
import { monthName } from '@/components/myPay/i18n';

const TILES = ['worked', 'present', 'late', 'early', 'missed', 'pending', 'absent', 'off'];

export const printPay = () => {
  document.body.classList.add('printing-pay');
  const done = () => { document.body.classList.remove('printing-pay'); window.removeEventListener('afterprint', done); };
  window.addEventListener('afterprint', done);
  window.print();
};

const Tiles = ({ s, t }) => (
  <div className="grid grid-cols-4 sm:grid-cols-8 gap-2" data-testid="mypay-summary">
    {TILES.map((k) => (
      <div key={k} className={`rounded-lg px-2 py-2 text-center ${k === 'worked' ? 'bg-[#2D5F4F] text-white' : TONE[k]}`} data-testid={`mypay-tile-${k}`}>
        <div className="text-lg font-bold tabular-nums">{s[k]}</div><div className="text-[11px] leading-tight">{t(k)}</div>
      </div>
    ))}
  </div>
);

/** One staff month: month picker, summary tiles, day-by-day check in / out and pay. Used by the staff self-view and HR lookup. */
export const MonthView = ({ data, t, lang, onMonth, busy }) => (
  <div className="space-y-5 pay-print-area" data-testid="mypay-month-view">
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <div className="text-2xl font-bold text-[#1A1F2E]" data-testid="mypay-staff-name">{data.staff.staff_name}</div>
        <div className="text-sm text-gray-600">{t('staffId')} <b className="font-mono" data-testid="mypay-staff-id">{data.staff.staff_id}</b> · {data.staff.section}{data.staff.designation ? ` · ${data.staff.designation}` : ''}</div>
        <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Clock3 className="w-3.5 h-3.5" />{t('shift')} {data.shift.start}–{data.shift.end}</div>
      </div>
      <Button variant="outline" size="sm" onClick={printPay} className="print:hidden" data-testid="mypay-print-btn"><Printer className="w-4 h-4 mr-1" />{t('print')}</Button>
    </div>
    <div className="flex flex-wrap gap-2 print:hidden" data-testid="mypay-months">
      {data.months.map((m) => (
        <button key={m} type="button" onClick={() => onMonth(m)} disabled={busy}
          className={`rounded-full border px-3 py-1 text-sm transition-colors ${m === data.month ? 'border-[#2D5F4F] bg-[#2D5F4F] text-white' : 'border-gray-300 bg-white hover:bg-[#F7F3EA]'}`}
          data-testid={`mypay-month-${m}`}>{monthName(m, lang)}</button>
      ))}
      {busy && <Loader2 className="w-5 h-5 animate-spin text-amber-600 self-center" />}
    </div>
    <div className="flex items-center gap-2 text-lg font-semibold text-[#1A1F2E]"><CalendarDays className="w-5 h-5 text-[#2D5F4F]" />{monthName(data.month, lang)}</div>
    {data.is_current && <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900" data-testid="mypay-so-far">{t('soFar')}</p>}
    <Tiles s={data.summary} t={t} />
    <div className="space-y-2">
      <h2 className="text-base font-semibold text-[#1A1F2E]">{t('attendance')} <span className="text-sm font-normal text-gray-500">· {data.summary.hours} {t('hours')}</span></h2>
      <AttendanceTable days={data.days} total={data.pay.total} fx={data.pay.fixed} t={t} lang={lang} />
    </div>
    <div className="space-y-2">
      <h2 className="text-base font-semibold text-[#1A1F2E]">{t('pay')}</h2>
      <PaySection pay={data.pay} t={t} />
    </div>
  </div>
);

export default MonthView;
